import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import api from "@/lib/api";
import { Section, GlassCard, fadeUp } from "@/components/ui-kit";

const CATEGORIES = ["All", "Driver Tips", "Fleet Management", "Industry News", "Company Updates", "Business Growth"];

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => cat === "All" ? posts : posts.filter((p) => p.category === cat), [posts, cat]);

  return (
    <div data-testid="blog-list-page">
      <section className="pt-32 pb-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-[#3B82F6]">The GK Fleet Journal</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="mt-5 font-display text-5xl md:text-7xl tracking-tighter font-semibold leading-[1.02]">
            Notes from the road.
          </motion.h1>
        </div>
      </section>

      <Section>
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-sm transition ${
                cat === c ? "bg-[#3B82F6] text-white" : "bg-white/5 text-[#D1D5DB] hover:bg-white/10 border border-white/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-[#9CA3AF]">No posts yet in this category.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <motion.div key={p.id || p.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}>
                  <GlassCard className="h-full p-0 overflow-hidden">
                    {p.cover_image && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                      </div>
                    )}
                    <div className="p-6">
                      <p className="text-xs tracking-[0.2em] uppercase text-[#3B82F6]">{p.category}</p>
                      <h3 className="mt-3 font-display text-2xl leading-snug">{p.title}</h3>
                      <p className="mt-3 text-[#9CA3AF] line-clamp-3">{p.excerpt}</p>
                      <p className="mt-5 text-xs text-[#9CA3AF] flex items-center gap-2"><Calendar className="w-3 h-3" /> {formatDate(p.created_at)}</p>
                    </div>
                  </GlassCard>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return (
    <div className="pt-40 max-w-3xl mx-auto px-6 text-center">
      <p className="font-display text-3xl">Post not found.</p>
      <button onClick={() => nav("/blog")} className="mt-6 text-[#3B82F6]">← Back to blog</button>
    </div>
  );
  if (!post) return <div className="pt-40 text-center text-[#9CA3AF]">Loading…</div>;

  return (
    <div data-testid="blog-post-page">
      <section className="pt-28 pb-12 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <p className="mt-8 text-xs tracking-[0.25em] uppercase text-[#3B82F6]">{post.category}</p>
          <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tighter font-semibold leading-[1.05]">{post.title}</h1>
          <p className="mt-5 text-[#9CA3AF] text-sm flex items-center gap-2"><Calendar className="w-3 h-3" /> {formatDate(post.created_at)} · {post.author}</p>
        </div>
      </section>
      {post.cover_image && (
        <div className="max-w-4xl mx-auto px-6 mt-10">
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img src={post.cover_image} alt={post.title} className="w-full object-cover" />
          </div>
        </div>
      )}
      <article className="max-w-3xl mx-auto px-6 py-16 prose-invert">
        {post.content.split("\n").filter(Boolean).map((para, i) => (
          <p key={i} className="text-[#D1D5DB] text-lg leading-relaxed mb-5">{para}</p>
        ))}
      </article>
    </div>
  );
}
